/**
 * The selection model shared by the desktop and Explorer's folder view.
 *
 * Explorer only ever selected one thing here before, so this is where the
 * rules for selecting several arrive: Ctrl toggles one, Shift takes a range
 * from the anchor, and a bare click replaces the lot. All pure — the surfaces
 * hold the state and hand it back in, which is what lets the desktop and a
 * folder window share the behaviour without sharing a store.
 */

export interface SelectionState {
  /** Paths, exactly as they appear on the nodes. */
  paths: ReadonlySet<string>;
  /** Where a Shift-range measures from. */
  anchor: string | null;
}

export interface Modifiers {
  ctrl: boolean;
  shift: boolean;
}

export const EMPTY_SELECTION: SelectionState = { paths: new Set(), anchor: null };

export function isEmpty(state: SelectionState): boolean {
  return state.paths.size === 0;
}

/** Just this one, anchored on it. */
export function selectOnly(path: string): SelectionState {
  return { paths: new Set([path]), anchor: path };
}

export function selectAll(order: readonly string[]): SelectionState {
  return { paths: new Set(order), anchor: order[order.length - 1] ?? null };
}

/** Everything that wasn't selected, and nothing that was. */
export function invert(state: SelectionState, order: readonly string[]): SelectionState {
  const paths = new Set(order.filter((path) => !state.paths.has(path)));
  return { paths, anchor: state.anchor };
}

/** The run of items between two paths, in the order they are laid out. */
function range(order: readonly string[], from: string, to: string): string[] {
  const a = order.indexOf(from);
  const b = order.indexOf(to);
  if (a === -1 || b === -1) return b === -1 ? [] : [to];
  return order.slice(Math.min(a, b), Math.max(a, b) + 1);
}

/**
 * Pressing an item.
 *
 * The subtle one is the last case: pressing something that is *already*
 * selected leaves the selection alone, because the press might be the start of
 * a drag and picking up five files has to survive putting the mouse down on
 * one of them. Collapsing to the one item is `release`'s job, and only if no
 * drag followed — which is exactly how Windows has always behaved.
 */
export function press(
  state: SelectionState,
  path: string,
  order: readonly string[],
  mods: Modifiers,
): SelectionState {
  if (mods.shift && state.anchor !== null) {
    const run = range(order, state.anchor, path);
    const paths = mods.ctrl ? new Set([...state.paths, ...run]) : new Set(run);
    // The anchor survives a range so the run can be widened and narrowed.
    return { paths, anchor: state.anchor };
  }

  if (mods.ctrl) {
    const paths = new Set(state.paths);
    if (paths.has(path)) paths.delete(path);
    else paths.add(path);
    return { paths, anchor: path };
  }

  if (state.paths.has(path)) return state;
  return selectOnly(path);
}

/**
 * Letting go of an item without having dragged it: a plain click on one of
 * several selected items drops the rest.
 */
export function release(
  state: SelectionState,
  path: string,
  mods: Modifiers,
): SelectionState {
  if (mods.ctrl || mods.shift) return state;
  if (state.paths.size <= 1) return state;
  if (!state.paths.has(path)) return state;
  return selectOnly(path);
}

/**
 * One frame of a rubber band. `base` is the selection as it stood when the
 * band started, so sweeping back over an item un-selects it again.
 */
export function sweep(
  base: SelectionState,
  hits: readonly string[],
  additive: boolean,
): SelectionState {
  const paths = additive ? new Set([...base.paths, ...hits]) : new Set(hits);
  return { paths, anchor: hits[hits.length - 1] ?? base.anchor };
}

/** Drop anything that is no longer on screen — after a move, or a delete. */
export function prune(state: SelectionState, order: readonly string[]): SelectionState {
  const live = new Set(order);
  let changed = false;
  const paths = new Set<string>();
  for (const path of state.paths) {
    if (live.has(path)) paths.add(path);
    else changed = true;
  }
  if (!changed) return state;
  return { paths, anchor: state.anchor && live.has(state.anchor) ? state.anchor : null };
}
